import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { cn } from '../../../lib/cn';
import {
  TASK_PRIORITIES,
  TASK_STATUSES,
  taskSchema,
  type TaskInput,
  type TaskProjectOption,
  type TaskRow,
} from './tasks.schema';
import styles from './tasks.module.css';

const STATUS_LABELS: Record<(typeof TASK_STATUSES)[number], string> = {
  todo: 'לעשות',
  in_progress: 'בעבודה',
  blocked: 'חסום',
  done: 'בוצע',
  cancelled: 'בוטל',
};

const PRIORITY_LABELS: Record<(typeof TASK_PRIORITIES)[number], string> = {
  low: 'נמוכה',
  normal: 'רגילה',
  high: 'גבוהה',
  urgent: 'דחופה',
};

const DEFAULT_VALUES: TaskInput = {
  title: '',
  description: '',
  status: 'todo',
  priority: undefined,
  project_id: '',
  due_date: '',
};

function valuesFromTask(task: TaskRow | null): TaskInput {
  if (!task) return DEFAULT_VALUES;
  return {
    title: task.title,
    description: task.description ?? '',
    status: task.status,
    priority: task.priority ?? undefined,
    project_id: task.project_id ?? '',
    due_date: task.due_date ?? '',
  };
}

interface TaskFormProps {
  task: TaskRow | null;
  projects: TaskProjectOption[];
  /** Pre-selects the project on a new task (opened from a project page). */
  defaultProjectId?: string;
  formId: string;
  disabled?: boolean;
  onSubmit: (values: TaskInput) => void | Promise<void>;
}

export function TaskForm({
  task,
  projects,
  defaultProjectId,
  formId,
  disabled = false,
  onSubmit,
}: TaskFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TaskInput>({
    resolver: zodResolver(taskSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    const values = valuesFromTask(task);
    reset(task || !defaultProjectId ? values : { ...values, project_id: defaultProjectId });
  }, [task, defaultProjectId, reset]);

  return (
    <form id={formId} className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className={styles.field}>
        <label htmlFor="task-title">כותרת</label>
        <input
          id="task-title"
          type="text"
          className={cn(styles.input, errors.title && styles.invalid)}
          disabled={disabled || isSubmitting}
          {...register('title')}
        />
        {errors.title ? <p className={styles.error}>{errors.title.message}</p> : null}
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor="task-status">סטטוס</label>
          <select
            id="task-status"
            className={styles.input}
            disabled={disabled || isSubmitting}
            {...register('status')}
          >
            {TASK_STATUSES.map((status) => (
              <option key={status} value={status}>
                {STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor="task-priority">עדיפות</label>
          <select
            id="task-priority"
            className={styles.input}
            disabled={disabled || isSubmitting}
            {...register('priority')}
          >
            <option value="">ללא</option>
            {TASK_PRIORITIES.map((priority) => (
              <option key={priority} value={priority}>
                {PRIORITY_LABELS[priority]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor="task-project">פרויקט</label>
          <select
            id="task-project"
            className={styles.input}
            disabled={disabled || isSubmitting}
            {...register('project_id')}
          >
            <option value="">ללא (משימה אישית)</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor="task-due-date">תאריך יעד</label>
          <input
            id="task-due-date"
            type="date"
            dir="ltr"
            className={styles.input}
            disabled={disabled || isSubmitting}
            {...register('due_date')}
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="task-description">תיאור</label>
        <textarea
          id="task-description"
          rows={5}
          className={cn(styles.input, styles.textarea, errors.description && styles.invalid)}
          disabled={disabled || isSubmitting}
          {...register('description')}
        />
        {errors.description ? <p className={styles.error}>{errors.description.message}</p> : null}
      </div>
    </form>
  );
}
